import { useMemo, useState } from "react";
import { ArrowRight, Search, X } from "lucide-react";
import { Shell } from "./Shell";
import { FadeUp, TypeBadge } from "./Sections";
import items from "@/content/insightsData.json";

const fmtDate = (d) => {
  const [y, m] = d.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[Number(m) - 1]} ${y}`;
};

const FEATURED_SLUGS = [
  "energy-crossroads-a-harc-podcast-series",
  "harc-releases-new-report-on-building-a-critical-energy-and-water-workforce-in-harris-county",
];

const Highlight = ({ text, q }) => {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[#C9DA2D]/60 text-inherit">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
};

export default function InsightsPage() {
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState("All");
  const [type, setType] = useState("All");
  const [sort, setSort] = useState("newest");

  const topics = useMemo(
    () =>
      ["All", ...new Set(items.map((i) => i.topic))].sort((a, b) =>
        a === "All" ? -1 : b === "All" ? 1 : a.localeCompare(b)
      ),
    []
  );
  const types = useMemo(
    () =>
      ["All", ...new Set(items.map((i) => i.type))].sort((a, b) =>
        a === "All" ? -1 : b === "All" ? 1 : a.localeCompare(b)
      ),
    []
  );

  const filtered = useMemo(() => {
    const list = items.filter(
      (i) =>
        (topic === "All" || i.topic === topic) &&
        (type === "All" || i.type === type) &&
        (!q || i.title.toLowerCase().includes(q.toLowerCase()))
    );
    if (sort === "oldest") return [...list].sort((a, b) => a.lastmod.localeCompare(b.lastmod));
    if (sort === "az") return [...list].sort((a, b) => a.title.localeCompare(b.title));
    return [...list].sort((a, b) => b.lastmod.localeCompare(a.lastmod));
  }, [q, topic, type, sort]);

  const filtering = q || topic !== "All" || type !== "All";
  const featured = items.filter((i) => FEATURED_SLUGS.some((s) => i.url.includes(s)));
  const clear = () => {
    setQ("");
    setTopic("All");
    setType("All");
    setSort("newest");
  };

  const sortLabel = { newest: "Newest first", oldest: "Oldest first", az: "A–Z" }[sort];

  return (
    <Shell>
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-24" data-testid="insights-page">
        <FadeUp>
          <p className="mb-5 flex items-center text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
            <span className="mr-2.5 inline-block h-2.5 w-2.5 bg-[#FFA100]" aria-hidden="true" />
            Insights & Resources
          </p>
          <h1 className="harc-heading max-w-3xl text-4xl font-medium leading-[1.1] tracking-tight text-[#004778] md:text-6xl">
            Ideas, Research, and Perspectives That Move Us Forward
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#5C6B72] md:text-lg md:font-light">
            Explore the latest thinking from HARC's experts: research, articles, podcasts, and
            conversations that make complex issues easier to understand and turn knowledge into
            informed action.
          </p>
          <p className="mt-4 max-w-2xl text-xs italic text-[#5C6B72]">
            Demonstration library: {items.length} real HARC items with live links. Topics and types
            are auto-assigned from page URLs for this mockup; the production taxonomy is validated
            during content triage.
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-12">
          <div className="flex flex-wrap items-center gap-4 border border-[#004778]/25 bg-white p-5">
            <div className="relative min-w-[240px] flex-1">
              <Search
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5C6B72]"
                aria-hidden="true"
              />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by keyword…"
                aria-label="Search insights by keyword"
                data-testid="insights-search-input"
                className="w-full border border-[#004778]/30 bg-transparent py-2.5 pl-10 pr-4 text-sm text-[#022A42] focus:outline-none focus:ring-2 focus:ring-[#004778]"
              />
            </div>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              aria-label="Filter by content type"
              data-testid="insights-type-filter"
              className="border border-[#004778]/30 bg-white px-4 py-2.5 text-sm font-semibold text-[#022A42] focus:outline-none focus:ring-2 focus:ring-[#004778]"
            >
              {types.map((t) => (
                <option key={t} value={t}>
                  {t === "All" ? "All types" : t}
                </option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort results"
              data-testid="insights-sort"
              className="border border-[#004778]/30 bg-white px-4 py-2.5 text-sm font-semibold text-[#022A42] focus:outline-none focus:ring-2 focus:ring-[#004778]"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="az">A–Z</option>
            </select>
            {filtering && (
              <button
                type="button"
                onClick={clear}
                data-testid="insights-clear-filters"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#004778] transition-colors hover:text-[#FFA100]"
              >
                <X size={14} aria-hidden="true" /> Clear
              </button>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filter by topic">
            {topics.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTopic(t)}
                data-testid={`insights-topic-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                aria-pressed={topic === t}
                className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] transition-colors ${
                  topic === t
                    ? "border-[#004778] bg-[#004778] text-white"
                    : "border-[#004778]/40 text-[#004778] hover:border-[#004778]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </FadeUp>

        {!filtering && (
          <FadeUp delay={0.15} className="mt-14">
            <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C6B72]">
              Featured
            </p>
            <div className="grid gap-5 md:grid-cols-2">
              {featured.map((f) => (
                <a
                  key={f.url}
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group border border-[#004778]/25 bg-white p-7 transition-colors hover:border-[#004778]"
                  data-testid="insights-featured-card"
                >
                  <TypeBadge type={f.type} />
                  <p className="harc-heading mt-4 text-xl leading-snug text-[#022A42]">{f.title}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#004778]">
                    Read on harcresearch.org
                    <ArrowRight
                      size={13}
                      className="transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </a>
              ))}
            </div>
          </FadeUp>
        )}

        <div className="mt-14">
          <p
            className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C6B72]"
            data-testid="insights-count"
          >
            {filtered.length} of {items.length} items · {sortLabel}
          </p>
          <div role="list" data-testid="insights-results">
            {filtered.slice(0, 60).map((it) => (
              <a
                key={it.url}
                role="listitem"
                href={it.url}
                target="_blank"
                rel="noopener noreferrer"
                className="insight-row"
                data-testid="insights-row"
              >
                <TypeBadge type={it.type} />
                <span>
                  <span className="harc-heading block text-lg leading-snug text-[#022A42]">
                    <Highlight text={it.title} q={q} />
                  </span>
                  {it.summary && (
                    <span className="mt-1.5 block max-w-2xl text-sm font-normal normal-case leading-relaxed tracking-normal text-[#5C6B72]">
                      {it.summary}
                    </span>
                  )}
                  <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5C6B72]">
                    {it.topic}
                  </span>
                </span>
                <span className="text-xs uppercase tracking-[0.1em] text-[#5C6B72]">
                  {fmtDate(it.lastmod)}
                </span>
                <ArrowRight size={18} className="row-arrow text-[#FFA100]" aria-hidden="true" />
              </a>
            ))}
            {filtered.length > 60 && (
              <p className="border-t border-[#004778]/20 py-5 text-sm italic text-[#5C6B72]">
                Showing the first 60 of {filtered.length} results. Refine your search or filters
                to narrow further.
              </p>
            )}
            <div className="border-t border-[#004778]/20" aria-hidden="true" />
          </div>
          {filtered.length === 0 && (
            <div
              className="border border-dashed border-[#004778]/40 p-10 text-center"
              data-testid="insights-empty"
            >
              <p className="harc-heading text-xl text-[#004778]">No items match these filters.</p>
              <button
                type="button"
                onClick={clear}
                className="mt-4 rounded-full bg-[#FFA100] px-6 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-[#022A42] transition-colors hover:bg-[#D98A00]"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
